import type { GeneralFunction } from '@asherng/common-types';
import type { ObserverInstanceCallback } from './types';

const observerMap = new Map<
  string,
  {
    id: string;
    observer: IntersectionObserver;
    elements: Map<Element, Array<ObserverInstanceCallback>>;
  }
>();

const RootIds: WeakMap<Element | Document, string> = new WeakMap();

let rootId = 0;

let unsupportedValue: boolean | undefined = undefined;

export const defaultFallbackInView = (inView: boolean | undefined): void => {
  unsupportedValue = inView;
};

const getRootId = (root: IntersectionObserverInit['root']) => {
  if (!root) {
    return '0';
  }

  if (RootIds.has(root)) {
    return RootIds.get(root);
  }

  rootId += 1;

  RootIds.set(root, rootId.toString());

  return RootIds.get(root);
};

export const optionsToId = (options: IntersectionObserverInit): string =>
  (Object.keys(options) as (keyof IntersectionObserverInit)[])
    .sort()
    .filter(key => options[key] !== undefined)
    .map(key => {
      return `${key}_${key === 'root' ? getRootId(options.root) : options[key]}`;
    })
    .toString();

const createObserver = (options: IntersectionObserverInit) => {
  const id = optionsToId(options);

  let instance = observerMap.get(id);

  if (!instance) {
    const elements = new Map<Element, Array<ObserverInstanceCallback>>();

    let thresholds: number[] | readonly number[] = [];

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const inView =
          entry.isIntersecting &&
          thresholds.some(threshold => entry.intersectionRatio >= threshold);

        if ((options as any).trackVisibility && typeof (entry as any).isVisible === 'undefined') {
          (entry as any).isVisible = inView;
        }

        elements.get(entry.target)?.forEach(callback => {
          callback(inView, entry);
        });
      });
    }, options);

    thresholds =
      observer.thresholds ||
      (Array.isArray(options.threshold) ? options.threshold : [options.threshold || 0]);

    instance = {
      id,
      observer,
      elements
    };

    observerMap.set(id, instance);
  }

  return instance;
};

export const observe = <E extends Element>(
  element: E,
  callback: ObserverInstanceCallback,
  options: IntersectionObserverInit = {},
  fallbackInView = unsupportedValue
): GeneralFunction => {
  if (typeof window.IntersectionObserver === 'undefined' && fallbackInView !== undefined) {
    const bounds = element.getBoundingClientRect();

    callback(fallbackInView, {
      isIntersecting: fallbackInView,
      target: element,
      intersectionRatio: typeof options.threshold === 'number' ? options.threshold : 0,
      time: 0,
      boundingClientRect: bounds,
      intersectionRect: bounds,
      rootBounds: bounds
    });

    return () => undefined;
  }

  // An observer with the same options can be reused, so lets use this fact
  const { id, observer, elements } = createObserver(options);

  // Register the callback listener for this element
  const callbacks = elements.get(element) || [];

  if (!elements.has(element)) {
    elements.set(element, callbacks);
  }

  callbacks.push(callback);
  observer.observe(element);

  return () => {
    // Remove the callback from the callback list
    callbacks.splice(callbacks.indexOf(callback), 1);

    if (callbacks.length === 0) {
      // No more callback exists for element, so destroy it
      elements.delete(element);
      observer.unobserve(element);
    }

    if (elements.size === 0) {
      observer.disconnect();
      observerMap.delete(id);
    }
  };
};
